import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import type { Permission } from "./stores/auth";

export interface NavItem {
    label: string;
    to: string;
    description?: string;
    permissions?: Permission[];
}

export const navItems: NavItem[] = [
    { label: "nav.home.title", description: "", to: "/home" },
    {
        label: "nav.content.title",
        description: "nav.content.description",
        to: "/content",
        permissions: [
            "massage_program/view",
            "training_program/view",
            "training_category/view",
            "page/view",
            "component/view"
        ]
    },
    {
        label: "nav.markets.title",
        description: "nav.markets.description",
        to: "/markets",
        permissions: ["market/view"]
    },
    {
        label: "nav.chairs.title",
        description: "nav.chairs.description",
        to: "/chairs",
        permissions: ["chair/view"]
    },
    {
        label: "nav.customers.title",
        description: "nav.customers.description",
        to: "/customers",
        permissions: ["customer/view"]
    },
    {
        label: "nav.sellers.title",
        description: "nav.sellers.description",
        to: "/sellers",
        permissions: ["seller/view"]
    },
    {
        label: "nav.docs.title",
        description: "nav.docs.description",
        to: "/docs"
    },
    {
        label: "nav.admin.title",
        description: "nav.admin.description",
        to: "/admin",
        permissions: ["invite/view", "user/view"]
    }
];

export const routes: RouteRecordRaw[] = [
    {
        path: "/",
        name: "login",
        component: () => import("./pages/Login.vue")
    },
    {
        path: "/invite/:token",
        name: "invite",
        component: () => import("./pages/Invite.vue")
    },
    {
        path: "/home",
        name: "home",
        component: () => import("./pages/Home.vue")
    },
    {
        path: "/content",
        name: "content",
        redirect: { name: "content-massage" },
        children: [
            {
                path: "massage",
                name: "content-massage",
                component: () => import("./pages/content/massage/index.vue")
            },
            {
                path: "massage/save",
                name: "content-massage-save",
                component: () => import("./pages/content/massage/Save.vue")
            },
            {
                path: "massage/save/:id",
                name: "content-massage-save-id",
                component: () => import("./pages/content/massage/Save.vue")
            },
            {
                path: "training",
                name: "content-training",
                component: () => import("./pages/content/training/index.vue")
            },
            {
                path: "training/save",
                name: "content-training-save",
                component: () => import("./pages/content/training/Save.vue")
            },
            {
                path: "training/save/:id",
                name: "content-training-save-id",
                component: () => import("./pages/content/training/Save.vue")
            },
            {
                path: "category",
                name: "content-category",
                component: () => import("./pages/content/category/index.vue")
            },
            {
                path: "category/save",
                name: "content-category-save",
                component: () => import("./pages/content/category/Save.vue")
            },
            {
                path: "category/save/:id",
                name: "content-category-save-id",
                component: () => import("./pages/content/category/Save.vue")
            },
            {
                path: "static",
                name: "content-massage2",
                component: () => import("./pages/content/static/index.vue")
            },
            {
                path: "static/save/:id",
                name: "content-static-save-id",
                component: () => import("./pages/content/static/Save.vue")
            },
            {
                path: "uicomponents",
                name: "content-uicomponents",
                component: () => import("./pages/content/uicomponents/index.vue")
            },
            {
                path: "uicomponents/save/:id",
                name: "content-uicomponents-save-id",
                component: () => import("./pages/content/uicomponents/Save.vue")
            }
        ]
    },
    {
        path: "/markets",
        children: [
            {
                name: "market",
                path: "",
                component: () => import("./pages/market/index.vue")
            },
            {
                path: "save",
                name: "market-save",
                component: () => import("./pages/market/Save.vue")
            },
            {
                path: "save/:id",
                name: "market-save-id",
                component: () => import("./pages/market/Save.vue")
            },
            {
                path: "content-tree/:id",
                name: "market-content-tree-id",
                component: () => import("./pages/market/ContentTree.vue")
            }
        ]
    },
    {
        path: "/chairs",
        name: "chairs",
        redirect: { name: "chairs-overview" },
        children: [
            {
                path: "",
                name: "chairs-overview",
                component: () => import("./pages/chairs/index.vue")
            },
            {
                path: "save/:id",
                name: "chairs-save-id",
                component: () => import("./pages/chairs/Save.vue")
            },
            {
                path: "details/:id",
                name: "chairs-details-id",
                component: () => import("./pages/chairs/Details.vue")
            }
        ]
    },
    {
        path: "/customers",
        name: "customers",
        redirect: { name: "customers-overview" },
        children: [
            {
                path: "",
                name: "customers-overview",
                component: () => import("./pages/customers/index.vue")
            },
            {
                path: "save",
                name: "customers-save",
                component: () => import("./pages/customers/Save.vue")
            },
            {
                path: "save/:id",
                name: "customers-save-id",
                component: () => import("./pages/customers/Save.vue")
            }
        ]
    },
    {
        path: "/sellers",
        name: "sellers",
        redirect: { name: "sellers-overview" },
        children: [
            {
                path: "",
                name: "sellers-overview",
                component: () => import("./pages/sellers/index.vue")
            },
            {
                path: "save",
                name: "sellers-save",
                component: () => import("./pages/sellers/Save.vue")
            },
            {
                path: "save/:id",
                name: "sellers-save-id",
                component: () => import("./pages/sellers/Save.vue")
            }
        ]
    },
    {
        path: "/admin",
        name: "admin",
        redirect: { name: "admin-users" },
        component: () => import("./pages/admin/index.vue"),
        children: [
            {
                path: "invites",
                name: "admin-invites",
                component: () => import("./pages/admin/invites/index.vue")
            },
            {
                path: "settings",
                name: "admin-settings",
                component: () => import("./pages/admin/settings/index.vue")
            },
            {
                path: "users",
                name: "admin-users",
                component: () => import("./pages/admin/users/index.vue")
            }
        ]
    },
    {
        path: "/docs",
        name: "docs",
        component: () => import("./pages/docs/index.vue")
    },
    {
        path: "/:pathMatch(.*)",
        name: "not-found",
        component: () => import("./pages/NotFound.vue")
    }
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

export default router;
